import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { LuListChecks } from 'react-icons/lu';
import { EmptyState, LoadingSpinner } from 'components/common';
import {
  getPifComparison, loadPifCheckFromAi, reextractProposal, removeProposalDocument, reviewPifCheck,
  savePifCheck, uploadProposalDocument,
} from 'redux/crm/project/contractPifCheckThunks';
import ProposalFilesCard from './ProposalFilesCard';
import BuildingPifCheck from './BuildingPifCheck';
import PifCheckStep from './PifCheckStep';
import PifSourcePicker from './PifSourcePicker';
import { stepStates } from './pifCheckModel';

const POLL_MS = 4000;
const POLL_MAX_MS = 10 * 60 * 1000;
const RUNNING = ['pending', 'processing'];

// PIF check of a contract, in the order the work is done: 1 choose the CO# whose
// PIF is the reference, 2 upload the Proposal Sales sent back, 3 compare and
// confirm, building by building.
const PifCheckTab = ({ project, jobBuilding, contract, onChanged }) => {
  const dispatch = useDispatch();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [fileBusy, setFileBusy] = useState(false);
  const [manualEntry, setManualEntry] = useState(false);
  const mountedRef = useRef(true);

  // jobBuilding / contract are absent before the contract exists.
  const jobBuildingId = jobBuilding?.id;
  const contractId = contract?.id;
  const ids = { projectId: project.id, jobBuildingId, contractId };

  // Bumped by every mutation: a poll that started before a save must not land
  // after it and put the old values back.
  const seqRef = useRef(0);

  const load = useCallback(async () => {
    const seq = seqRef.current;
    try {
      const payload = await dispatch(getPifComparison({ projectId: project.id, jobBuildingId, contractId })).unwrap();
      if (mountedRef.current && seq === seqRef.current) {
        setData(payload);
        setError(null);
      }
    } catch (e) {
      if (mountedRef.current) setError(typeof e === 'string' ? e : 'Could not load the PIF check.');
    }
  }, [dispatch, project.id, jobBuildingId, contractId]);

  useEffect(() => {
    mountedRef.current = true;
    load();
    return () => { mountedRef.current = false; };
  }, [load]);

  // Keep refreshing while the AI is reading a proposal, so the comparison fills
  // in by itself when it finishes.
  // Capped: a run stuck in "pending" (worker down) must not poll forever.
  const aiRunning = (data?.proposal?.extractions || []).some((e) => RUNNING.includes(e.status));
  useEffect(() => {
    if (!aiRunning) return undefined;
    const startedAt = Date.now();
    const timer = setInterval(() => {
      if (Date.now() - startedAt > POLL_MAX_MS) clearInterval(timer);
      else load();
    }, POLL_MS);
    return () => clearInterval(timer);
  }, [aiRunning, load]);

  const replaceBuilding = (building) => setData((prev) => prev && {
    ...prev,
    buildings: prev.buildings.map((b) => (b.building_id === building.building_id ? building : b)),
  });

  // Resolves to true on success so the caller can keep unsaved edits on failure.
  const runBuildingAction = async (thunk, args) => {
    seqRef.current += 1;
    try {
      const res = await dispatch(thunk({ ...ids, ...args })).unwrap();
      if (res?.building) replaceBuilding(res.building);
      if (res?.message) toast.success(res.message);
      onChanged?.();
      return true;
    } catch (e) {
      toast.error(typeof e === 'string' ? e : 'The PIF check could not be updated.');
      return false;
    }
  };

  const runFileAction = async (thunk, args) => {
    seqRef.current += 1;
    setFileBusy(true);
    try {
      const res = await dispatch(thunk({ ...ids, ...args })).unwrap();
      if (mountedRef.current) setData(res);
      if (res?.message) toast.success(res.message);
      onChanged?.();
    } catch (e) {
      toast.error(typeof e === 'string' ? e : 'The proposal file action failed.');
    } finally {
      if (mountedRef.current) setFileBusy(false);
    }
  };

  // Step 2 commits step 1: a CO# that was only suggested is saved before the
  // upload, so the comparison the user then reviews is pinned to what they saw.
  const upload = async (files) => {
    const unsaved = data.buildings.filter((b) => b.selected_adaptation_id && !b.selection_saved);
    try {
      await Promise.all(unsaved.map((b) => dispatch(savePifCheck({
        ...ids, buildingId: b.building_id, data: { adaptation_id: b.selected_adaptation_id },
      })).unwrap()));
    } catch (e) {
      toast.error(typeof e === 'string' ? e : 'Could not save the chosen CO#.');
      return;
    }
    await runFileAction(uploadProposalDocument, { files });
  };

  if (error && !data) {
    return <EmptyState icon={LuListChecks} title="PIF check unavailable" description={error} />;
  }
  if (!data) return <div className="tw-py-10 tw-flex tw-justify-center"><LoadingSpinner /></div>;
  if (data.buildings.length === 0) {
    return <EmptyState icon={LuListChecks} title="No buildings"  description="No building is waiting for a check here." />;
  }

  const canEdit = !!data.permissions?.can_edit;
  const steps = stepStates(data, { manualEntry });

  return (
    <div className="tw-flex tw-flex-col tw-gap-3">
      <PifCheckStep
        number={1}
        title="Choose the PIF"
        description="For each building, the CO# whose PIF the Proposal must match."
        state={steps.pick}
      >
        <PifSourcePicker
          projectId={project.id}
          buildings={data.buildings}
          canEdit={canEdit}
          locked={steps.aiRunning || fileBusy}
          onChange={(b, adaptationId) => runBuildingAction(savePifCheck, { buildingId: b.building_id, data: { adaptation_id: adaptationId } })}
        />
      </PifCheckStep>

      <PifCheckStep
        number={2}
        title="Upload the Proposal"
        description="The Proposal Sales sent back, Word or PDF. AI reads its Project Details tables (building parameters, design loads)."
        state={steps.upload}
        lockedReason={steps.uploadLockedReason}
      >
        <ProposalFilesCard
          documents={data.proposal.documents}
          extractions={data.proposal.extractions}
          canEdit={canEdit}
          busy={fileBusy}
          onUpload={upload}
          onRemove={(doc) => runFileAction(removeProposalDocument, { documentId: doc.id })}
          onReextract={(blobId) => runFileAction(reextractProposal, { blobId })}
        />
        {canEdit && !steps.hasProposalData && data.proposal.documents.length === 0 && !manualEntry && (
          <p className="tw-m-0 tw-px-4 tw-pb-3 tw-text-xs tw-text-slate-500">
            No file?{' '}
            <button type="button" className="tw-p-0 tw-border-0 tw-bg-transparent tw-text-primary-600 tw-cursor-pointer"
              onClick={() => setManualEntry(true)}>
              Type the Proposal values by hand
            </button>
          </p>
        )}
      </PifCheckStep>

      <PifCheckStep
        number={3}
        title="Compare and confirm"
        description="Each building against the PIF chosen in step 1. Correct any value the AI misread, then mark the building as checked."
        state={steps.compare}
        lockedReason={steps.compareLockedReason}
      >
        {steps.aiRunning && !steps.hasProposalData ? (
          <p className="tw-m-0 tw-px-4 tw-py-6 tw-flex tw-items-center tw-justify-center tw-gap-2 tw-text-sm tw-text-slate-500">
            <LoadingSpinner size="sm" /> AI is reading the Proposal — the comparison appears here when it is done.
          </p>
        ) : (
          <div className="tw-flex tw-flex-col tw-gap-3 tw-p-3">
            {data.buildings.map((building) => (
              <BuildingPifCheck
                key={building.building_id}
                building={building}
                fields={data.fields}
                sections={data.sections}
                canEdit={canEdit}
                // The comparison is about to change under the user — no editing meanwhile.
                editLocked={steps.aiRunning || fileBusy}
                onSave={(b, payload) => runBuildingAction(savePifCheck, { buildingId: b.building_id, data: payload })}
                onLoadFromAi={(b) => runBuildingAction(loadPifCheckFromAi, { buildingId: b.building_id })}
                onReview={(b, opts) => runBuildingAction(reviewPifCheck, { buildingId: b.building_id, ...opts })}
              />
            ))}
          </div>
        )}
      </PifCheckStep>
    </div>
  );
};

PifCheckTab.propTypes = {
  project:     PropTypes.object.isRequired,
  jobBuilding: PropTypes.object,
  contract:    PropTypes.object,
  // Called after any save / review / file action, e.g. to refresh a list row.
  onChanged:   PropTypes.func,
};

export default PifCheckTab;
